/**
 * Minimal markdown-to-HTML renderer.
 * Handles common patterns: bold, italic, inline code, line breaks, links.
 *
 * SECURITY (M-3): the output is injected into the DOM via
 * `dangerouslySetInnerHTML`, so the renderer itself must neutralize
 * injection:
 *   - every HTML metacharacter (`& < > " '`) is escaped BEFORE any tag is
 *     built — the quotes are what make `href="$2"` attribute injection
 *     impossible;
 *   - link URLs are scheme-whitelisted (`http`, `https`, `mailto` or
 *     scheme-less/relative). Any other scheme (`javascript:`, `data:`,
 *     `vbscript:`, `file:`, case-insensitive) drops the `href` entirely.
 */
export function renderMarkdown(content: string): string {
  if (!content) return '';

  return content
    // Escape HTML entities first (quotes included so no attribute can be
    // injected through the href attribute).
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    // Bold: **text**
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text*
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Inline code: `text`
    .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
    // Links: [text](url) — href is scheme-whitelisted (M-3).
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (_match: string, text: string, rawUrl: string) => {
        const href = sanitizeHref(rawUrl);
        const hrefAttr = href === null ? '' : ` href="${href}"`;
        return `<a${hrefAttr} class="text-blue-600 underline">${text}</a>`;
      },
    )
    // Line breaks
    .replace(/\n/g, '<br/>');
}

/**
 * Scheme-whitelist for link destinations.
 *
 * Allowed: `http:`, `https:`, `mailto:` and scheme-less (relative) URLs.
 * Any other scheme (`javascript:`, `data:`, `vbscript:`, mixed case, whitespace
 * padding, ...) is rejected → `null`, so the caller drops the `href` attribute
 * and no clickable/executable surface survives (M-3).
 */
export function sanitizeHref(rawUrl: string): string | null {
  const url = rawUrl.trim();
  if (url.length === 0) return null;

  const scheme = url.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  if (scheme) {
    const lowered = scheme[1].toLowerCase();
    return lowered === 'http' || lowered === 'https' || lowered === 'mailto'
      ? url
      : null;
  }
  return url; // relative reference (e.g. /docs/intro, #section)
}